/**
 * Helper functions for parsing AI API responses
 */

import logger from './logger'

/**
 * Parses AI transcript response and returns structured data
 * @param {Object|string} aiResult - The AI response data
 * @returns {Object} Parsed transcript data with { transcript, isValid, summary }
 */
export const parseAITranscriptResponse = (aiResult) => {
  if (!aiResult) {
    return { transcript: null, isValid: false, summary: null }
  }

  if (typeof aiResult === 'string') {
    try {
      aiResult = JSON.parse(aiResult)
    } catch (e) {
      logger.error('Failed to parse AI response:', e)
      return { transcript: null, isValid: false, summary: null }
    }
  }

  if (aiResult.transcript && aiResult.summary) {
    return {
      transcript: typeof aiResult.transcript === 'string' ? aiResult.transcript : null,
      summary: typeof aiResult.summary === 'string' ? aiResult.summary : (aiResult.summary.generated_summary || null),
      isValid: true
    }
  }

  if (aiResult.summary) {
    const summaryData = aiResult.summary
    
    if (summaryData.valid_call === true) {
      return {
        transcript: typeof summaryData.transcript === 'string' ? summaryData.transcript : null,
        summary: typeof summaryData.generated_summary === 'string' ? summaryData.generated_summary : null,
        isValid: true
      }
    } else if (summaryData.valid_call === false) {
      return {
        transcript: null,
        summary: null,
        isValid: false
      }
    }
  }

  if (aiResult.valid_call === true) {
    return {
      transcript: typeof aiResult.transcript === 'string' ? aiResult.transcript : null,
      summary: typeof aiResult.generated_summary === 'string' ? aiResult.generated_summary : null,
      isValid: true
    }
  } else if (aiResult.valid_call === false) {
    return {
      transcript: null,
      summary: null,
      isValid: false
    }
  }

  let summaryText = null
  if (typeof aiResult.generated_summary === 'string') {
    summaryText = aiResult.generated_summary
  } else if (typeof aiResult.summary === 'string') {
    summaryText = aiResult.summary
  } else if (aiResult.summary && typeof aiResult.summary.generated_summary === 'string') {
    summaryText = aiResult.summary.generated_summary
  }

  return {
    transcript: typeof aiResult.transcript === 'string' ? aiResult.transcript : null,
    summary: summaryText,
    isValid: true
  }
}

/**
 * Parses existing AI data from selectedRow
 * @param {Object} selectedRow - The selected row data
 * @param {string} field - The field name to parse ('ai_transcript' or 'ai_summary')
 * @returns {Object} Parsed data with { transcript, isValid, summary }
 */
export const parseExistingAIData = (selectedRow, field) => {
  if (!selectedRow || !selectedRow[field]) {
    return { transcript: null, isValid: true, summary: null }
  }

  try {
    let aiData = selectedRow[field]
    
    if (typeof aiData === 'string') {
      if (aiData.startsWith('{') || aiData.startsWith('[')) {
        aiData = JSON.parse(aiData)
        
        return parseAITranscriptResponse(aiData)
      } else {
        return field === 'ai_transcript' 
          ? { transcript: typeof aiData === 'string' ? aiData : null, isValid: true, summary: null }
          : { transcript: null, isValid: true, summary: typeof aiData === 'string' ? aiData : null }
      }
    } else if (typeof aiData === 'object') {
      return parseAITranscriptResponse(aiData)
    }
  } catch (e) {
    logger.error(`Failed to parse existing ${field}:`, e)
    return { transcript: null, isValid: true, summary: null }
  }

  return { transcript: null, isValid: true, summary: null }
}

/**
 * Gets a standard invalid call message
 * @returns {string} The standard message for invalid calls
 */
export const getInvalidCallMessage = () => {
  return 'This call contains no meaningful conversation as it may be a spam call, accidental call, voicemail, hangup, or similar non-conversational interaction.'
}